import React, { useEffect, useRef, useState } from "react";
import "video.js/dist/video-js.css";
import style from "../style.module.scss";
import classnames from "classnames";
import { keyHandler } from "@jmc/utils/utils/keyHandler";
import { Brightcove, Props } from "@jmc/solid-design-system/src/components/organisms/VideoPlayer/Brightcove/Brightcove";
import { CookieConsent } from "@jmc/solid-design-system/src/utils/cookieConsent";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { PreloaderLayout } from "@jmc/solid-design-system/src/components/organisms/VideoPlayer/PreloaderLayout/PreloaderLayout";

/*
 * Preload the Brightcove videos by displaying a placeholder with the image thumbnail
 * This will avoid performance problems in pages with a lot of videos, cause we won't
 * load them until the user clicks on them.
 */
export const BrightcovePreload: React.FunctionComponent<Props> = (props: any) => {
    const [clicked, setClicked] = useState(false);
    const [accepted, setAccepted] = useState("loading");

    const {
        thumbnail,
        videoId,
        className = null,
        isAudio = false,
        showLargePlayButton = true,
        warningMessage = undefined,
        cookieAccept = null,
        play = false,
        autoplay = false,
    } = props;

    const ref = useRef<HTMLDivElement>(null);
    const { jnjFullBranded } = useJnjBranding();

    const clickHandler = () => {
        setClicked(true);
    };

    useEffect(() => {
        const scriptStatus = (): void => {
            const activeGroups = (window as any)?.OptanonActiveGroups as string;
            const currentStatus =
                activeGroups?.split(",")?.includes(`${CookieConsent.FUNTIONAL_COOKIES}`) || activeGroups === undefined
                    ? "ready"
                    : "blocked";
            setAccepted(currentStatus);
            cookieAccept && cookieAccept(currentStatus === "ready");
        };

        scriptStatus();
        window.addEventListener("OptanonActiveGroupsChanged", scriptStatus);
        return () => {
            window.removeEventListener("OptanonActiveGroupsChanged", scriptStatus);
        };
    });

    const component =
        clicked || play || autoplay || !thumbnail ? (
            <Brightcove
                {...props}
                startPlaying={thumbnail || clicked || play || autoplay}
                playerId={jnjFullBranded ? "eHSYOivXo" : props.playerId}
            />
        ) : (
            <div
                ref={ref}
                onClick={() => accepted !== "blocked" && clickHandler()}
                onKeyDown={(e: KeyboardEvent): void =>
                    keyHandler(e.key, () => {
                        accepted !== "blocked" && clickHandler();
                    })
                }
                className={classnames(
                    className,
                    style.player,
                    !isAudio || thumbnail ? style.videoPlayer : style.ajs,
                    !showLargePlayButton || (!thumbnail && isAudio) ? style.hidePlay : style.playButton,
                    accepted === "blocked" && style.blocked,
                )}
                data-test-id={`Video.Brightcove.Preload.${videoId}`}
                role="button"
                tabIndex={0}
            >
                {accepted === "blocked" && warningMessage && (
                    <div
                        id={`cookie-warning-${videoId}`}
                        className={style.warning}
                        data-test-id={`VideoPlayer.${videoId}`}
                    >
                        {warningMessage}
                    </div>
                )}

                {accepted !== "blocked" && (
                    <PreloaderLayout thumbnail={thumbnail} dataTestId={`VideoPlayer.${videoId}`} />
                )}
            </div>
        );

    return (
        <div className={style.preloadWrapper}>
            <div className={style.preloadContent}>{component}</div>
        </div>
    );
};

export default BrightcovePreload;
